import { onUnmounted, ref, type Ref } from 'vue'
import { $accountService } from '@/lib/services/accountService'
import { useUserStore } from '@/lib/stores'

const POLL_INTERVAL_MS = 60 * 60 * 1000 // 1 hour

/**
 * Normalize package_features for comparison.
 * Drops `timestamp` since it is not counted as a package change.
 */
const normalizePackageData = (data: Record<string, any>) => {
  const { timestamp: _timestamp, ...rest } = data

  return Object.fromEntries(
    Object.entries(rest).map(([key, packageData]) => {
      if (key === 'add_ons') {
        return [
          key,
          [...packageData].sort((a, b) =>
            a.feature_key.localeCompare(b.feature_key)
          ),
        ]
      }

      if (packageData?.features) {
        return [
          key,
          {
            ...packageData,
            features: [...packageData.features].sort((a, b) =>
              a.feature_key.localeCompare(b.feature_key)
            ),
          },
        ]
      }

      return [key, packageData]
    })
  )
}

export function usePackageFeaturesPolling(): {
  hasPackageUpdate: Ref<boolean>
} {
  const userStore = useUserStore()
  const hasPackageUpdate = ref(false)

  // Poll only when we have an account to fetch package_features for
  if (!userStore.getAccountId) {
    return { hasPackageUpdate }
  }

  let pollTimer: ReturnType<typeof setInterval> | undefined

  const stopPolling = () => {
    if (pollTimer !== undefined) {
      clearInterval(pollTimer)
      pollTimer = undefined
    }
  }

  const pollPackageFeatures = async () => {
    try {
      const response = await $accountService.fetchPackageFeatures()
      const packageData = response.data.data as Record<string, any>
      const storedPackageData = userStore.getTrailPackageFeatures as Record<string, any>
      const newData = normalizePackageData(packageData)
      const storedData = normalizePackageData(storedPackageData)
      const hasChanged = JSON.stringify(newData) !== JSON.stringify(storedData)
      if (hasChanged) {
        hasPackageUpdate.value = true
        stopPolling()
      }
    } catch (err) {
      console.error('Failed to poll package features:', err)
    }
  }

  pollTimer = setInterval(pollPackageFeatures, POLL_INTERVAL_MS)

  onUnmounted(stopPolling)

  return { hasPackageUpdate }
}
